import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { AppPermissions, IProfile, IUser } from '../models';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class ProfileService {
    currentUser?: IUser;
    profile: IProfile;
    accessDenied = false;

    constructor(
        private httpClient: HttpClient,
        private apiService: ApiService,
    ) {}

    fetchCurrentUser(): Observable<IUser> {
        return this.apiService.fetchMe().pipe(
            tap((user) => {
                this.accessDenied = false;
                this.currentUser = user;
            }),
            catchError((error) => {
                this.currentUser = undefined;
                if ((error as HttpErrorResponse).status === 503 && error.error && error.error.type === 'Maintenance Mode') {
                    this.accessDenied = true;
                } else if ((error as HttpErrorResponse).status === 401) {
                    this.accessDenied = true;
                }
                return throwError(() => error);
            }),
        );
    }

    fetchProfile(): Observable<IProfile> {
        // If profile is already cached, return it as an Observable
        if (this.profile) {
            return new Observable<IProfile>((observer) => {
                observer.next(this.profile);
                observer.complete();
            });
        }
        return this.httpClient.get<IProfile>('https://graph.microsoft.com/v1.0/me').pipe(tap((profile) => (this.profile = profile)));
    }

    hasUserManagementPermission(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canReadUsers) && permissions.includes(AppPermissions.canWriteUsers);
    }

    canExecuteSql(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canExecuteSql);
    }

    canWriteTemplate(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canWriteTemplate);
    }

    canWriteMetadata(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canWriteMetadata);
    }

    canReadDashboard(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canReadDashboard);
    }

    canWriteConfig(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canWriteConfig);
    }

    canUploadAdhocReports(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canUploadAdhocReports);
    }

    canShareAdhocReports(): boolean {
        const permissions = this.currentUser?.permissions ?? [];
        return permissions.includes(AppPermissions.canShareAdhocReports);
    }
}
